/* Atlas v3 — DotGlobe: a slowly drifting orthographic dot-globe, the "Earth
   downstairs" counterpart to the Overview starfield. Built from the SAME
   committed worldDots.js grid the Cycle map uses (no new data, no three.js) —
   the flat grid is read back as lon/lat and projected onto a sphere on a
   <canvas>, drifting one revolution per ~90s. The GEO_PINS signal cities
   render in amber; land is warm-muted with limb fade.

   WHERE IT'S ALLOWED: empty states and auth/no-data surfaces ONLY — never
   behind live data (same one-signature-per-screen rule as the starfield).
   Respects prefers-reduced-motion (static frame, no RAF loop). */
import { useEffect, useRef } from 'react'
import { MAP_W, MAP_H, GEO_PINS, WORLD_DOTS } from './worldDots'
import { REDUCE } from './lib'

// Grid → lon/lat (the dotted-map grid is equirectangular enough for texture).
const toLonLat = ([x, y]) => [(x / MAP_W) * 360 - 180, 90 - (y / MAP_H) * 180]
const LAND = WORLD_DOTS.map(toLonLat)
const CITIES = Object.values(GEO_PINS).map(({ x, y }) => toLonLat([x, y]))

const DEG = Math.PI / 180
const REV_MS = 90_000 // one revolution — drift, not a spinner

export function DotGlobe({ size = 150, style }) {
  const ref = useRef(null)

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    canvas.width = size * dpr
    canvas.height = size * dpr
    const ctx = canvas.getContext('2d')
    ctx.scale(dpr, dpr)

    // Resolve the design tokens once (canvas can't read CSS vars directly).
    const css = getComputedStyle(canvas)
    const landCol = `hsl(${css.getPropertyValue('--studio-text-subtle') || '35 8% 46%'}`
    const cityCol = `hsl(${css.getPropertyValue('--studio-accent') || '41 54% 51%'}`

    const R = size / 2 - 2
    const cx = size / 2, cy = size / 2

    const draw = (t) => {
      const rot = ((t % REV_MS) / REV_MS) * 360
      ctx.clearRect(0, 0, size, size)
      for (const pass of [{ pts: LAND, col: landCol, r: size / 210, aMax: 0.5 }, { pts: CITIES, col: cityCol, r: size / 110, aMax: 1 }]) {
        for (const [lon, lat] of pass.pts) {
          const lam = (lon + rot) * DEG, phi = lat * DEG
          const z = Math.cos(phi) * Math.cos(lam)
          if (z <= 0) continue // back hemisphere
          const px = cx + R * Math.cos(phi) * Math.sin(lam)
          const py = cy - R * Math.sin(phi)
          ctx.fillStyle = `${pass.col} / ${(0.15 + 0.85 * z) * pass.aMax})`
          ctx.beginPath()
          ctx.arc(px, py, pass.r, 0, Math.PI * 2)
          ctx.fill()
        }
      }
    }

    if (REDUCE) { draw(REV_MS * 0.13); return } // static frame, Pacific-to-Asia in view
    let raf
    const loop = (t) => { draw(t); raf = requestAnimationFrame(loop) }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [size])

  return <canvas ref={ref} aria-hidden style={{ width: size, height: size, display: 'block', ...style }} />
}
